import { createContext, useCallback, useContext, useSyncExternalStore } from "react";
import { ChatFileMessage } from "context/adminContext";
import { NotificationContext } from "context/notificationContext";

import { getOriginalUrl, saveFile } from "./utils";

export type DownloadState = "idle" | "loading" | "loaded" | "error";

type Entry = { state: DownloadState; blobUrl?: string };

const IDLE: Entry = { state: "idle" };
const RELOAD_THROTTLE_MS = 10_000;

// Перезапрос истории открытого чата: ссылки на файлы живут 15 минут
export const ChatHistoryReloadContext = createContext<() => void>(() => undefined);

// Скачанные файлы помним только до перезагрузки страницы
const entries = new Map<number, Entry>();
const listeners = new Set<() => void>();
// Один notification на всю очередь ошибок — до следующей успешной загрузки
let isErrorShown = false;
let lastReloadAt = 0;

const setEntry = (id: number, entry: Entry) => {
  entries.set(id, entry);
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// Истёкший action JWT: берём свежие ссылки из истории, но не чаще раза в 10 с
export const useFreshFileUrls = () => {
  const reloadChatHistory = useContext(ChatHistoryReloadContext);
  return useCallback(() => {
    const now = Date.now();
    if (now - lastReloadAt < RELOAD_THROTTLE_MS) return;
    lastReloadAt = now;
    reloadChatHistory();
  }, [reloadChatHistory]);
};

export const useChatFileDownload = (file: ChatFileMessage | undefined) => {
  const { openNotificationWithIcon } = useContext(NotificationContext);
  const requestFreshUrls = useFreshFileUrls();
  const entry = useSyncExternalStore(subscribe, () => (file ? entries.get(file.id) : undefined) ?? IDLE);

  const fetchFile = useCallback(async () => {
    const cached = entries.get(file.id);
    if (cached?.blobUrl) return cached.blobUrl;
    if (cached?.state === "loading") return null;
    setEntry(file.id, { state: "loading" });
    try {
      const response = await fetch(getOriginalUrl(file));
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          requestFreshUrls();
        }
        throw new Error(`File download failed: ${response.status}`);
      }
      const blobUrl = URL.createObjectURL(await response.blob());
      setEntry(file.id, { state: "loaded", blobUrl });
      isErrorShown = false;
      return blobUrl;
    } catch (error) {
      console.error(error);
      setEntry(file.id, { state: "error" });
      if (!isErrorShown) {
        isErrorShown = true;
        openNotificationWithIcon("error", "Error while downloading file. Try again.");
      }
      return null;
    }
  }, [file, openNotificationWithIcon, requestFreshUrls]);

  const download = useCallback(async () => {
    const blobUrl = await fetchFile();
    if (blobUrl) saveFile(blobUrl, file.content);
  }, [fetchFile, file]);

  const open = useCallback(() => {
    if (entry.blobUrl) window.open(entry.blobUrl, "_blank");
  }, [entry.blobUrl]);

  return { state: entry.state, download, open };
};
